import {updateAndRunAnalysisStep} from 'analysis-tool/AnalysisActions';
import isAnalysisReadOnly from 'analysis-tool/isAnalysisReadOnly';
import {assertAndContinue} from 'Asserts';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PipelineGraphQL, PipelineStep, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {withLoadingScreen} from 'util/Promises';

const COMPATIBLE_STEP_TYPES = [
  PipelineStepType.CHART_VIEW,
  PipelineStepType.REGRESSION,
  PipelineStepType.TABLE_VIEW,
];

export function canEditTitle(analysis: PipelineGraphQL, analysisView: PipelineViewGraphQL): boolean {
  const lastNode = analysisView.lineage[analysisView.lineage.length - 1];
  return (
    !isAnalysisReadOnly(analysis) &&
    lastNode.step.statusCode === PipelineStepStatusCode.SUCCEEDED &&
    COMPATIBLE_STEP_TYPES.includes(lastNode.step.stepType)
  );
}

export default async function handleOnTitleChange(
  newTitle: string,
  analysis: PipelineGraphQL,
  analysisView: PipelineViewGraphQL
): Promise<void> {
  const lastNode = analysisView.lineage[analysisView.lineage.length - 1];

  if (
    !assertAndContinue(
      canEditTitle(analysis, analysisView),
      'View is not compatible with title change. Use canEditTitle to disable title change for this view at the call site.',
      {
        stepId: lastNode.step.id,
        stepStatusCode: lastNode.step.statusCode,
        stepType: lastNode.step.stepType,
      }
    )
  ) {
    return;
  }

  await withLoadingScreen(
    updateAndRunAnalysisStep(analysis.id, {
      stepId: lastNode.step.id,
      stepConfig: getNewConfigForStep(lastNode.step, newTitle),
    }),
    'Updating view...'
  );
}

function getNewConfigForStep<TPiplineStep extends PipelineStep>(
  step: TPiplineStep,
  newTitle: string
): TPiplineStep['config'] {
  switch (step.stepType) {
    case PipelineStepType.CHART_VIEW:
      return {
        ...step.config,
        chartName: newTitle,
      };
    case PipelineStepType.REGRESSION:
      return {
        ...step.config,
        name: newTitle,
      };
    case PipelineStepType.TABLE_VIEW:
      return {
        ...step.config,
        viewName: newTitle,
      };
    default:
      throw Error(`Incompatible step type ${step.stepType}`);
  }
}
